import React, { useEffect, useRef, useState } from 'react'
import { Container } from './Container'

// Early adopter badges instead of client logos
const earlyAdopters = [
  {
    name: 'Big 4 Audit',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=Big+4+Beta',
    type: 'Accounting'
  },
  {
    name: 'Internal Audit',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=IA+Beta',
    type: 'Finance'
  },
  {
    name: 'Risk Advisory',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=Risk+Beta',
    type: 'Consulting'
  },
  {
    name: 'Compliance',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=Compliance',
    type: 'Regulatory'
  },
  {
    name: 'SOX Testing',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=SOX+Beta',
    type: 'Compliance'
  },
  {
    name: 'IT Audit',
    logo: 'https://placehold.co/200x80/e5e7eb/475569?text=IT+Audit',
    type: 'Technology'
  }
]

// Beta tester testimonials (more realistic feedback from friends and colleagues)
const testimonials = [
  {
    content: "The automation capabilities for control testing could save us weeks of time on each engagement. I'm excited to see this tool develop further.",
    author: "Alex Thompson",
    role: "Senior Manager",
    company: "Big 4 Advisory",
    image: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    content: "As an internal audit professional, I've been looking for a tool that can streamline our documentation process. This has serious potential to transform how we work.",
    author: "Priya Sharma",
    role: "Internal Audit Director",
    company: "Beta Tester",
    image: "https://randomuser.me/api/portraits/women/46.jpg"
  },
  {
    content: "The AI-powered risk assessment is impressive even in this early stage. With some refinement, this could be a game-changer for the industry.",
    author: "James Wilson",
    role: "IT Audit Manager",
    company: "Early Access Program",
    image: "https://randomuser.me/api/portraits/men/65.jpg"
  }
]

export function SocialProof() {
  const [animateLogos, setAnimateLogos] = useState(false)
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const containerRef = useRef(null)
  const [showAllCards, setShowAllCards] = useState(false)

  // Logo animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setAnimateLogos(true)
        }
      },
      { threshold: 0.1 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 8000)

    return () => clearInterval(interval)
  }, [])

  return (
    <section ref={containerRef} className="bg-gradient-to-b from-white to-slate-50 py-24 overflow-hidden">
      <Container>
        {/* Section heading */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Early Adopters & Beta Testers
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Professionals from leading firms in audit and compliance are already providing valuable feedback on our platform.
          </p>
        </div>

        {/* Beta user badges - Mobile optimized */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Desktop view - grid layout */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {earlyAdopters.map((adopter, index) => (
              <div 
                key={adopter.name}
                className={`transition-all duration-700 transform ${
                  animateLogos ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative group h-full">
                  {/* Animated gradient border */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-30 group-hover:opacity-100 transition duration-300 group-hover:duration-200 animate-tilt"></div>
                  
                  {/* Card body */}
                  <div className="relative h-full flex flex-col bg-white rounded-xl shadow-lg border border-slate-100/80 backdrop-blur-sm p-6">
                    <div className="flex items-center justify-center mb-4">
                      {/* Icon/logo - could be replaced with real logos */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <span className="text-white font-bold text-xl">{adopter.name.charAt(0)}</span>
                      </div>
                    </div>
                    
                    <div className="text-center flex-grow">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{adopter.name}</h3>
                      <div className="text-sm text-gray-600 mb-3">{adopter.type}</div>
                      <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                        Beta Program
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile view - horizontal scroll with featured cards */}
          <div className="md:hidden">
            <div className="relative">
              {/* Horizontal scrollable row */}
              <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-hide">
                {earlyAdopters.slice(0, 3).map((adopter, index) => (
                  <div 
                    key={adopter.name}
                    className={`flex-shrink-0 w-[85%] max-w-[250px] transition-all duration-700 transform ${
                      animateLogos ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="relative group h-full">
                      {/* Animated gradient border */}
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-30 group-hover:opacity-100 transition duration-300 group-hover:duration-200 animate-tilt"></div>
                      
                      {/* Card body */}
                      <div className="relative h-full flex flex-col bg-white rounded-xl shadow-lg border border-slate-100/80 backdrop-blur-sm p-6">
                        <div className="flex items-center justify-center mb-4">
                          {/* Icon/logo */}
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xl">{adopter.name.charAt(0)}</span>
                          </div>
                        </div>
                        
                        <div className="text-center flex-grow">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{adopter.name}</h3>
                          <div className="text-sm text-gray-600 mb-3">{adopter.type}</div>
                          <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                            Beta Program
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* "View All" card */}
                <div 
                  className={`flex-shrink-0 w-[85%] max-w-[250px] transition-all duration-700 transform ${
                    animateLogos ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `300ms` }}
                >
                  <button 
                    onClick={() => setShowAllCards(true)}
                    className="relative h-full w-full flex flex-col bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-6 group"
                  >
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-blue-700">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                        </svg>
                      </div>
                    </div>
                    
                    <div className="text-center flex-grow">
                      <h3 className="text-lg font-semibold text-blue-900 mb-1">View All</h3>
                      <div className="text-sm text-blue-600">See all {earlyAdopters.length} early adopters</div>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* Gradient fade effect on edges */}
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none"></div>
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-50 to-transparent pointer-events-none"></div>
            </div>
            
            {/* Expanded view when "View All" is clicked */}
            {showAllCards && (
              <div className="mt-10">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <span>All Early Adopters</span>
                  <button 
                    onClick={() => setShowAllCards(false)} 
                    className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M14.78 14.78a.75.75 0 01-1.06 0L6.5 7.56v5.69a.75.75 0 01-1.5 0v-7.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5H7.56l7.22 7.22a.75.75 0 010 1.06z" clipRule="evenodd" />
                    </svg>
                  </button>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {earlyAdopters.map((adopter) => (
                    <div key={adopter.name} className="bg-white rounded-lg shadow-sm border border-slate-100 p-4">
                      <h4 className="font-medium text-gray-900">{adopter.name}</h4>
                      <p className="text-xs text-gray-600">{adopter.type}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Beta program metrics */}
        <div className="mt-20 mb-16">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-16 text-center lg:grid-cols-3">
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">Beta testers onboarded</dt>
              <dd className="order-first text-5xl font-semibold tracking-tight text-blue-600 sm:text-6xl">24</dd>
            </div>
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">Features in development</dt>
              <dd className="order-first text-5xl font-semibold tracking-tight text-blue-600 sm:text-6xl">15+</dd>
            </div>
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">Projected efficiency gain</dt>
              <dd className="order-first text-5xl font-semibold tracking-tight text-blue-600 sm:text-6xl">60%</dd>
            </div>
          </dl>
        </div>

        {/* Testimonial section - enhanced */}
        <div className="relative mt-24 overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 py-12 px-6 sm:py-16 sm:px-12 md:p-20 shadow-xl">
          {/* Background pattern */}
          <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="30" height="30" patternUnits="userSpaceOnUse">
                <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(255, 255, 255, 0.1)" strokeWidth="1"></path>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)"></rect>
          </svg>

          <div className="absolute top-4 right-8">
            <span className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-100 ring-1 ring-inset ring-blue-400/20">
              Beta Feedback
            </span>
          </div>

          {/* Testimonial content */}
          <div className="relative">
            <div className="flex flex-col items-center">
              {/* Quote icon */}
              <svg className="h-10 w-10 mb-6 text-white opacity-20" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>

              {/* Testimonial slides */}
              <div className="relative h-auto min-h-[200px] w-full">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.author}
                    className={`${
                      index === activeTestimonial
                        ? 'opacity-100 translate-y-0 z-10'
                        : 'opacity-0 translate-y-10 -z-10 absolute inset-0'
                    } transition-all duration-1000 ease-in-out flex flex-col items-center`}
                  >
                    <blockquote className="text-xl font-medium text-white text-center max-w-3xl mb-8">
                      "{testimonial.content}"
                    </blockquote>
                    <div className="flex items-center">
                      <img
                        className="h-12 w-12 rounded-full border-2 border-white/30 mr-4"
                        src={testimonial.image}
                        alt={testimonial.author}
                      />
                      <div className="text-left">
                        <div className="font-semibold text-white">{testimonial.author}</div>
                        <div className="text-blue-200 text-sm">
                          {testimonial.role}, {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Testimonial navigation dots */}
              <div className="flex space-x-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`h-2 w-2 rounded-full transition-all duration-300 ${
                      activeTestimonial === index
                        ? 'bg-white w-6'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

// Add this CSS to your global styles file or styled-components
// @keyframes tilt {
//   0%, 100% { transform: rotate(0deg); }
//   25% { transform: rotate(0.5deg); }
//   75% { transform: rotate(-0.5deg); }
// }
// .animate-tilt { animation: tilt 10s infinite linear; } 